const PORTRAIT_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_142de766-7447-4fb5-b920-10398996bff4/artifacts/0pp8u9ck_fondo-cyborg-8k-vertical.webp";

// AlivanAvatar animates with audio amplitude for lip-sync feel
// amplitude: number 0..1 representing current audio loudness
const AlivanAvatar = ({ isSpeaking = false, amplitude = 0, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24'
  };

  // Amplify amplitude for visual effect (0..1)
  const amp = Math.min(1, amplitude * 1.6);
  const scale = 1 + amp * 0.06; // subtle breathing/talking scale
  const glowOpacity = isSpeaking ? 0.25 + amp * 0.55 : 0;
  const glowBlur = 8 + amp * 20;
  const borderIntensity = isSpeaking ? Math.max(0.5, amp) : 0.5;

  // Mouth ripple: a small red pulse near the bottom-center of the avatar
  // representing "lip movement". Grows with amplitude.
  const mouthScale = 0.15 + amp * 0.85;
  const mouthOpacity = isSpeaking ? Math.min(1, 0.35 + amp * 0.9) : 0;

  return (
    <div
      className={`relative ${sizeClasses[size]}`}
      style={{
        transform: `scale(${scale})`,
        transition: 'transform 80ms linear',
        filter: isSpeaking ? `drop-shadow(0 0 ${glowBlur}px rgba(255, 0, 51, ${glowOpacity}))` : 'none'
      }}
      data-testid="alivan-avatar"
      data-speaking={isSpeaking ? 'true' : 'false'}
      data-amplitude={amplitude.toFixed(3)}
    >
      {/* Hexagonal frame with dynamic border intensity */}
      <div
        className={`hexagon-clip ${sizeClasses[size]} overflow-hidden border-2 relative`}
        style={{
          borderColor: `rgba(255, 0, 51, ${borderIntensity})`,
          transition: 'border-color 80ms linear'
        }}
      >
        <img
          src={PORTRAIT_URL}
          alt="Alivan"
          className="w-full h-full object-cover"
          draggable={false}
        />

        {/* Mouth animation overlay - a subtle red glow bloom over the lip area */}
        {isSpeaking && (
          <div
            className="absolute pointer-events-none"
            style={{
              // Position over the approximate lip area of the portrait
              left: '38%',
              top: '68%',
              width: '24%',
              height: '10%',
              background: 'radial-gradient(ellipse at center, rgba(255, 0, 51, 0.9) 0%, rgba(255, 0, 51, 0.4) 40%, transparent 75%)',
              opacity: mouthOpacity,
              transform: `scale(${mouthScale})`,
              transformOrigin: 'center',
              transition: 'opacity 60ms linear, transform 60ms linear',
              mixBlendMode: 'screen',
              filter: 'blur(2px)'
            }}
            data-testid="alivan-mouth-glow"
          />
        )}

        {/* Vertical "voice waveform" bars near mouth area */}
        {isSpeaking && size !== 'sm' && (
          <div
            className="absolute pointer-events-none flex items-end justify-center gap-[2px]"
            style={{
              left: '42%',
              top: '73%',
              width: '16%',
              height: '6%',
              opacity: Math.min(1, 0.4 + amp * 1.2)
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-[1.5px] bg-neon-red"
                style={{
                  height: `${20 + amp * 80 * (0.5 + Math.sin(Date.now() / 100 + i) * 0.5)}%`,
                  transition: 'height 60ms linear',
                  boxShadow: '0 0 3px rgba(255, 0, 51, 0.8)'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Outer glow ring that pulses with amplitude */}
      {isSpeaking && (
        <div
          className="absolute inset-0 hexagon-clip pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255, 0, 51, 0.3) 0%, transparent 70%)',
            opacity: 0.3 + amp * 0.7,
            filter: `blur(${6 + amp * 8}px)`,
            transition: 'opacity 80ms linear, filter 80ms linear'
          }}
        />
      )}
    </div>
  );
};

export default AlivanAvatar;
